


import { cn } from '../../utils/cn.ts';
import { Icon } from './Icon.tsx';

const NativeButton = 'button';

export interface PillOption<T extends string = string> {
    value: T;
    label: string;
    variant?: 'default' | 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'success' | 'warning' | 'info' | 'purple' | 'amber' | 'blue' | 'emerald' | 'red' | 'gray';
    icon?: string;
    ariaLabel?: string;
    className?: string;
    selectedClassName?: string;
}

interface PillSelectorProps<T extends string = string> {
    options: PillOption<T>[];
    value?: T | T[]; // Single value or array of values
    onChange: (value: T) => void; // Returns the clicked value
    ariaLabel?: string;
    multiSelect?: boolean;
    className?: string;
    size?: 'sm' | 'md' | 'lg';
    selectedIcon?: string;
}

/**
 * PillSelector - A reusable primitive for selecting values from a set of options using interactive pills.
 * Can be used for single or multi-select scenarios.
 */
export const PillSelector = <T extends string = string>({ 
    options, 
    value, 
    onChange, 
    ariaLabel,
    multiSelect = false,
    className = '',
    size = 'md',
    selectedIcon,
}: PillSelectorProps<T>) => {
    const usesMultiSelectSemantics = multiSelect || Array.isArray(value);
    
    const isSelected = (optionValue: T) => {
        if (Array.isArray(value)) {
            return value.includes(optionValue);
        }
        return value === optionValue;
    };

    const handleSelect = (optionValue: T) => {
        onChange(optionValue);
    };

    const getSizeClasses = () => {
        switch (size) {
            case 'sm': return 'text-xs px-2 py-1';
            case 'lg': return 'text-base px-4 py-2';
            default: return 'text-sm px-3 py-1.5';
        }
    };

    const getSelectedVariantClasses = (variant: PillOption<T>['variant']) => {
        switch (variant) {
            case 'danger':
            case 'red':
                return 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800';
            case 'success':
            case 'emerald':
                return 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800';
            case 'warning':
            case 'amber':
                return 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800';
            case 'info':
            case 'blue':
                return 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800';
            case 'purple':
                return 'bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-400 dark:border-purple-800';
            case 'primary':
                return 'bg-primary/10 text-primary border-primary/20 dark:bg-primary/20 dark:text-primary-light dark:border-primary/30';
            case 'secondary':
            case 'gray':
            case 'default':
            case undefined:
                return 'bg-gray-100 text-gray-900 border-gray-300 dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600';
            case 'outline':
            case 'ghost':
                return 'bg-white dark:bg-surface-dark text-primary border-primary/30';
        }
    };

    const pills = options.map((option) => {
        const selected = isSelected(option.value);

        return (
            <NativeButton
                key={option.value}
                type="button"
                onClick={() => handleSelect(option.value)}
                aria-label={option.ariaLabel}
                aria-checked={usesMultiSelectSemantics ? undefined : selected}
                aria-pressed={usesMultiSelectSemantics ? selected : undefined}
                role={usesMultiSelectSemantics ? undefined : 'radio'}
                className={cn(
                    `rounded-full border font-medium transition-all duration-200 ${getSizeClasses()}
                    ${selected
                        ? 'shadow-sm ring-1 ring-primary/20 ring-offset-1'
                        : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 dark:border-gray-700 dark:bg-surface-dark dark:text-gray-400 dark:hover:border-gray-600'
                    }
                    ${selected ? getSelectedVariantClasses(option.variant) : ''}
                    `,
                    selected ? option.selectedClassName : option.className
                )}
            >
                {option.icon ? <Icon name={option.icon} size="xs" /> : null}
                {selected && selectedIcon ? <Icon name={selectedIcon} size="xs" /> : null}
                {option.label}
            </NativeButton>
        );
    });

    if (usesMultiSelectSemantics) {
        return (
            <fieldset
                aria-label={ariaLabel}
                className={`m-0 flex min-w-0 flex-wrap gap-2 border-0 p-0 ${className}`}
            >
                {pills}
            </fieldset>
        );
    }

    return (
        <div
            aria-label={ariaLabel}
            className={`flex flex-wrap gap-2 ${className}`}
            role="radiogroup"
        >
            {pills}
        </div>
    );
};
